import React, { useEffect, useRef } from "react";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "textarea:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

const HEADING_ID = "deleteCommentConfirmModalLabel";
const ERROR_ID = "deleteCommentConfirmModalError";

/**
 * The single confirmation before any comment delete (TRD §2.2.5). Fully
 * React-controlled, following src/components/modal/StatusChangeNotesModal.js
 * exactly — own backdrop, FOCUSABLE_SELECTOR focus trap,
 * previouslyFocusedElementRef restore, submittingRef double-submit guard,
 * Escape as Cancel (blocked while busy). Deliberately NOT the legacy
 * Bootstrap data-API modal pattern (DeleteCheckListWarningModal.js,
 * actionCompleteConfirmationModal.js) and does not convert them (NG22).
 * `data-bs-backdrop="static"` is present as a plain attribute for parity
 * with that precedent, but nothing here depends on Bootstrap's JS — the
 * open/close/focus/keyboard behaviour is entirely this component's own.
 *
 * `isModeratorDelete` changes only the copy — it names the author and
 * states this is another user's comment (AC-F8.2, P1/F8). The caller
 * (ChecklistComments, CLCMT-F006) is the only place that decides when it
 * is true; P0 never passes it.
 *
 * Props:
 * - isOpen:            whether the dialog is shown
 * - authorName:        the comment author's display name, used only in
 *                       the moderator-delete copy
 * - isModeratorDelete: bool — P1/F8 only, changes wording only
 * - busy:              true while the caller's DELETE is in flight
 * - serverError:       string | null — rendered inline, never a toast
 * - onConfirm:         () => void
 * - onCancel:          () => void — Cancel, Escape, close button, or
 *                       backdrop-safe no-op
 */
const DeleteCommentConfirmModal = ({
  isOpen,
  authorName,
  isModeratorDelete,
  busy,
  serverError,
  onConfirm,
  onCancel,
}) => {
  const dialogRef = useRef(null);
  const confirmButtonRef = useRef(null);
  const previouslyFocusedElementRef = useRef(null);
  // Belt-and-braces guard against a double-click issuing two requests
  // before the caller's `busy` prop has propagated back down (AC-F5.7) —
  // the same pattern as StatusChangeNotesModal.js's submittingRef.
  const submittingRef = useRef(false);

  // Focus the confirm button on open (the destructive action should never
  // be the accidental default, but it is the element the user is most
  // likely to want next after choosing to delete); restore focus to the
  // triggering Delete control on close (AC-F5.9, AC-T17).
  useEffect(() => {
    if (isOpen) {
      previouslyFocusedElementRef.current = document.activeElement;
      submittingRef.current = false;
      const focusTimer = setTimeout(() => {
        confirmButtonRef.current?.focus();
      }, 0);
      return () => clearTimeout(focusTimer);
    }

    const previouslyFocused = previouslyFocusedElementRef.current;
    if (previouslyFocused instanceof HTMLElement) {
      previouslyFocused.focus();
    }
    previouslyFocusedElementRef.current = null;
    return undefined;
  }, [isOpen]);

  // Release the in-flight guard once the caller's request settles.
  useEffect(() => {
    if (!busy) {
      submittingRef.current = false;
    }
  }, [busy]);

  const handleCancel = () => {
    if (busy || submittingRef.current) return;
    onCancel();
  };

  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      handleCancel();
      return;
    }

    if (event.key === "Tab") {
      const dialog = dialogRef.current;
      if (!dialog) return;
      const focusable = Array.from(
        dialog.querySelectorAll(FOCUSABLE_SELECTOR)
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  };

  const handleConfirm = () => {
    if (busy || submittingRef.current) return;
    submittingRef.current = true;
    onConfirm();
  };

  if (!isOpen) return null;

  const confirmDisabled = Boolean(busy);
  // The action is stated as not undoable from the app in both copies
  // (deletion is a soft delete recoverable only by a DBA runbook, R3 —
  // never something the user themselves can undo).
  const bodyText = isModeratorDelete
    ? `This is ${authorName || "another user"}’s comment. Deleting it can’t be undone from the app.`
    : "This can’t be undone from the app.";

  return (
    <>
      <div className="modal-backdrop show delete-comment-confirm-modal-backdrop" />
      <div
        className="modal fade show d-block delete-comment-confirm-modal"
        id="deleteCommentConfirmModal"
        tabIndex="-1"
        role="dialog"
        aria-modal="true"
        aria-labelledby={HEADING_ID}
        aria-describedby={serverError ? ERROR_ID : undefined}
        data-bs-backdrop="static"
        onKeyDown={handleKeyDown}
      >
        <div className="modal-dialog modal-dialog-centered" ref={dialogRef}>
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id={HEADING_ID}>
                Delete comment?
              </h5>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                disabled={busy}
                onClick={handleCancel}
              />
            </div>
            <div className="modal-body">
              {serverError ? (
                <div className="alert alert-danger" role="alert" id={ERROR_ID}>
                  {serverError}
                </div>
              ) : null}
              <p className="delete-comment-confirm-modal-body-text">
                {bodyText}
              </p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                disabled={busy}
                onClick={handleCancel}
              >
                Cancel
              </button>
              <button
                type="button"
                ref={confirmButtonRef}
                className="btn btn-danger delete-comment-confirm-modal-confirm"
                disabled={confirmDisabled}
                onClick={handleConfirm}
              >
                {busy ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DeleteCommentConfirmModal;
